import type { GroupedRow } from '../types';
import { formatCurrency, formatNumber, formatPercent, formatDimensionValue } from '../utils';
import { FinancialEmptyState } from '../../../shared/components';

interface GroupedTableProps {
  data: GroupedRow[];
  dimensionLabel: string;
  dimensionKey: string;
  comparison?: { deltas: Array<{ dimensionValue: string; comparisonValue: number; deltaPercentage: number }> } | null;
  onRowClick?: (groupValue: string) => void;
  sortBy: 'total' | 'count' | 'name';
  sortOrder: 'asc' | 'desc';
  onSort: (by: 'total' | 'count' | 'name') => void;
  /** Optional callback to open date picker when no data is available */
  onOpenDatePicker?: () => void;
}

export function GroupedTable({
  data,
  dimensionLabel,
  dimensionKey,
  comparison,
  onRowClick,
  sortBy,
  sortOrder,
  onSort,
  onOpenDatePicker,
}: GroupedTableProps): JSX.Element {
  if (data.length === 0) {
    return (
      <FinancialEmptyState
        variant="no-data"
        onAction={onOpenDatePicker}
      />
    );
  }

  const maxTotal = Math.max(...data.map(d => Math.abs(d.total)));

  return (
    <div className="overflow-x-auto">
      <table className="table-financial w-full border-collapse text-sm">
        <thead>
          <tr className="bg-brand-dark-light">
            <th
              onClick={() => onSort('name')}
              className="text-left py-3.5 px-4 border-b-2 border-brand-primary cursor-pointer select-none text-white font-display"
            >
              {dimensionLabel}
              {sortBy === 'name' && (sortOrder === 'asc' ? ' ▲' : ' ▼')}
            </th>
            <th
              onClick={() => onSort('count')}
              className="text-right py-3.5 px-4 border-b-2 border-brand-primary cursor-pointer select-none w-[100px] text-white font-display"
            >
              Qtd
              {sortBy === 'count' && (sortOrder === 'asc' ? ' ▲' : ' ▼')}
            </th>
            <th
              onClick={() => onSort('total')}
              className="text-right py-3.5 px-4 border-b-2 border-brand-primary cursor-pointer select-none w-[150px] text-white font-display"
            >
              Total
              {sortBy === 'total' && (sortOrder === 'asc' ? ' ▲' : ' ▼')}
            </th>
            <th className="text-right py-3.5 px-4 border-b-2 border-brand-primary w-[180px] text-white font-display">
              % do Total
            </th>
            {comparison && (
              <th className="text-right py-3.5 px-4 border-b-2 border-brand-blue w-[150px] bg-brand-blue/10 text-brand-blue font-display">
                vs Anterior
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => {
            const displayValue = formatDimensionValue(row.dimensionValue, dimensionKey);
            const compRow = comparison?.deltas.find(d => d.dimensionValue === row.dimensionValue);
            const barWidth = maxTotal > 0 ? (Math.abs(row.total) / maxTotal) * 100 : 0;

            return (
              <tr
                key={row.dimensionValue}
                onClick={() => onRowClick?.(row.dimensionValue)}
                className={`
                  ${index % 2 === 0 ? 'bg-brand-dark' : 'bg-brand-dark-light'}
                  ${onRowClick ? 'table-row-clickable-financial' : 'table-row-financial'}
                `}
              >
                <td className="py-3 px-4 border-b border-brand-dark-lighter text-white">
                  <div className="flex items-center gap-2">
                    <span className="font-medium font-body">{displayValue}</span>
                    {onRowClick && <span className="text-brand-primary text-xs">→</span>}
                  </div>
                </td>
                <td className="py-3 px-4 border-b border-brand-dark-lighter text-right text-brand-gray font-body">
                  {formatNumber(row.count)}
                </td>
                <td className={`py-3 px-4 border-b border-brand-dark-lighter text-right font-semibold font-body ${row.total < 0 ? 'text-error' : 'text-white'}`}>
                  {formatCurrency(row.total)}
                </td>
                <td className="py-3 px-4 border-b border-brand-dark-lighter">
                  <div className="flex items-center justify-end gap-3">
                    <div className="w-[100px] h-2 bg-brand-dark-lighter rounded overflow-hidden">
                      <div
                        className={`h-full rounded transition-all duration-300 ${row.total < 0 ? 'bg-error' : 'bg-brand-primary'}`}
                        style={{ width: `${Math.min(100, barWidth)}%` }}
                      />
                    </div>
                    <span className="text-brand-gray min-w-[45px] text-right font-body">
                      {formatPercent(row.percentage)}
                    </span>
                  </div>
                </td>
                {comparison && (
                  <td className="py-3 px-4 border-b border-brand-dark-lighter text-right bg-brand-blue/5">
                    {compRow ? (
                      <div>
                        <div className="text-xs text-brand-gray font-body">
                          {formatCurrency(compRow.comparisonValue)}
                        </div>
                        <div className={`text-sm font-semibold font-body ${compRow.deltaPercentage >= 0 ? 'text-success' : 'text-error'}`}>
                          {compRow.deltaPercentage >= 0 ? '↑' : '↓'} {Math.abs(compRow.deltaPercentage).toFixed(1)}%
                        </div>
                      </div>
                    ) : (
                      <span className="text-brand-gray">-</span>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
